import React, {useEffect, useMemo, useState} from "react";
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent, Checkbox,
    CircularProgress, FormControl, FormControlLabel,
    Grid,
    IconButton, InputLabel, MenuItem, Paper, Select,
    TextField,
    Typography
} from "@mui/material";
import {Close} from "@mui/icons-material";
import {getCategoryIconByName, getFormErrorMessage} from "../../../../../configs/functions";
import {useAppContext} from "../../../../../contexts/AppContext";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";

const renderCard = (
    item,
    onClick,
    isSelecionado = false,
    onVariacaoChange,
    onQuantidadeChange,
    onJustificativaChange,
    onMarcarComoEntregueChange,
    selecionados,
    tipoRequisicao,
    justificativas,
    allowEntrega
) => {
    const getProdutoKey = (produto) =>
        produto.id_unico || `${produto.produto.id}-${produto.variacaoSelecionada || 'sem-var'}`;

    const filhos = item.produto.filhos || [];

    const variacoesSelecionadas = selecionados.filter(
        (s) => s.produto.id === item.produto.id
    );

    const atingiuLimite = tipoRequisicao === 'retirada'
        ? filhos.length > 0 && variacoesSelecionadas.length >= filhos.length
        : false;

    const isIndisponivel = !isSelecionado && (
        (
            tipoRequisicao === 'retirada'
                ? item.qtd_disponivel <= 0 || atingiuLimite
                : atingiuLimite
        ) || item.produto.status === 0
    );

    return (
        <Card
            key={getProdutoKey(item)}
            variant="outlined"
            sx={{
                mb: 1,
                cursor: isIndisponivel ? 'not-allowed' : 'pointer',
                opacity: isIndisponivel ? 0.5 : 1,
                pointerEvents: isIndisponivel && !isSelecionado ? 'none' : 'auto',
                backgroundColor:
                    tipoRequisicao === 'retirada' &&
                    !isSelecionado &&
                    item.qtd_disponivel === 0
                        ? '#ffe5e5'
                        : 'inherit',
                '&:hover': {
                    backgroundColor:
                        !isIndisponivel && item.qtd_disponivel > 0 ? '#f5f5f5' : undefined,
                },
                position: 'relative',
            }}
            onClick={!isIndisponivel && !isSelecionado ? () => onClick(item) : undefined}
        >
            <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Avatar
                    src={item.produto.img || null}
                    alt={item.produto.nome}
                    sx={{ bgcolor: '#ddd' }}
                >
                    {!item.produto.img && getCategoryIconByName(item.produto.icon_name, 50)}
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography fontWeight="bold">{item.produto.nome}</Typography>
                    <Typography variant="body2">CA: {item.produto.ca}</Typography>

                    {tipoRequisicao === 'retirada' && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                            Quantidade disponível: {item.qtd_disponivel}
                        </Typography>
                    )}

                    {isSelecionado && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                            {/* Primeira linha: Quantidade e Variação */}
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <TextField
                                    type="number"
                                    size="small"
                                    label="Quantidade"
                                    inputProps={{
                                        min: 1,
                                        max: tipoRequisicao === 'extra'
                                            ? 1
                                            : item.qtd_disponivel + item.quantidadeSelecionada,
                                    }}
                                    value={
                                        tipoRequisicao === 'extra'
                                            ? 1
                                            : item.quantidadeSelecionada
                                    }
                                    onChange={(e) =>
                                        onQuantidadeChange(
                                            item.id_unico,
                                            Math.max(1, parseInt(e.target.value) || 1)
                                        )
                                    }
                                    sx={{ width: 100 }}
                                    disabled={tipoRequisicao === 'extra'}
                                />

                                {isSelecionado &&
                                    tipoRequisicao === 'retirada' &&
                                    item.produto.filhos?.length > 0 && (
                                        <FormControl size="small" sx={{ minWidth: 200 }}>
                                            <InputLabel>Variação</InputLabel>
                                            <Select
                                                label="Variação"
                                                value={item.variacaoSelecionada || ''}
                                                onChange={(e) =>
                                                    onVariacaoChange(item, e.target.value)
                                                }
                                                variant="outlined"
                                            >
                                                {item.produto.filhos.map((filho) => {
                                                    const variacaoJaSelecionada = selecionados?.some(
                                                        (s) =>
                                                            s.produto.id === item.produto.id &&
                                                            s.variacaoSelecionada === filho.id &&
                                                            s.id_unico !== item.id_unico
                                                    );

                                                    if (variacaoJaSelecionada) return null;

                                                    return (
                                                        <MenuItem key={filho.id} value={filho.id}>
                                                            {filho?.variacoes?.tipo || filho?.nome || `Variação ${filho.id}`}
                                                        </MenuItem>
                                                    );
                                                })}
                                            </Select>
                                        </FormControl>
                                    )}
                            </Box>

                            {/* Segunda linha: Justificativa (apenas para tipo 'extra') */}
                            {tipoRequisicao === 'extra' && justificativas && (
                                <>
                                    <FormControl size="small" sx={{ minWidth: 300, marginTop: 1 }}>
                                        <InputLabel>Justificativa</InputLabel>
                                        <Select
                                            label="Justificativa"
                                            value={item.justificativaSelecionada || ''}
                                            onChange={(e) =>
                                                onJustificativaChange(item.id_unico, e.target.value)
                                            }
                                            variant="outlined"
                                            required
                                            error={!item.justificativaSelecionada}
                                        >
                                            {(justificativas &&justificativas.length > 0) ? (
                                                [
                                                    ...justificativas.filter(option => option.status !== 0).map(option => (
                                                        <MenuItem key={option.id} value={option.id}>
                                                            {option.nome || option.descricao}
                                                        </MenuItem>
                                                    )),
                                                    ...justificativas.filter(option => option.status === 0).map(option => (
                                                        <MenuItem key={option.id} value={option.id} disabled>
                                                            {option.nome || option.descricao} - (Inativo)
                                                        </MenuItem>
                                                    )),
                                                ]
                                            ) : (
                                                <MenuItem disabled>Nenhuma justificativa cadastrada</MenuItem>
                                            )}
                                        </Select>

                                        {!item.justificativaSelecionada && (
                                            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                                Justificativa é obrigatória
                                            </Typography>
                                        )}
                                    </FormControl>

                                    {allowEntrega && (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={item.marcarComoEntregue || false}
                                                    onChange={(e) => onMarcarComoEntregueChange(item.id_unico, e.target.checked)}
                                                    color="primary"
                                                />
                                            }
                                            label="Marcar como entregue"
                                            sx={{ display: 'block', mb: 1 }}
                                        />
                                    )}
                                </>
                            )}
                        </Box>
                    )}
                </Box>

                {isSelecionado && (
                    <IconButton onClick={() => onClick(item)} sx={{ alignSelf: 'flex-start' }}>
                        <Close />
                    </IconButton>
                )}
            </CardContent>
        </Card>
    );
};

export function NewRequisicaoThirdStep({
                                            isLoading,
                                            produtos,
                                            produtosSelecionados,
                                            setProdutosSelecionados,
                                            tipoRequisicao,
                                            justificativas,
                                            selectedJustificativa,
                                            setSelectedJustificativa,
                                            marcarEntregue,
                                            setMarcarEntregue,
                                            dialogOpen,
                                            selectedPessoa
                                        }) {
    const { dadosUsuario } = useAppContext();

    const [disponiveis, setDisponiveis] = useState([]);
    const [selecionados, setSelecionados] = useState(() => Array.isArray(produtosSelecionados) ? produtosSelecionados : []);
    const [filtro, setFiltro] = useState('');

    const [allowEntrega, setAllowEntrega] = useState(false);

    // Reset quando o dialog fechar/abrir
    useEffect(() => {
        if (dialogOpen === false) {
            setSelecionados([]);
            setProdutosSelecionados([]);
            setFiltro('');
        }
    }, [dialogOpen, setProdutosSelecionados]);

    // Não soma por categoria, usa quantidade do próprio produto
    const qtdPorProduto = disponiveis.length > 0 && disponiveis.reduce((acc, produto) => {
        acc[produto.produto.id] = produto.qtd_disponivel ?? produto.quantidade_maxima ?? 0;
        return acc;
    }, {});

    const disponiveisFiltrados = disponiveis.length > 0 && disponiveis
        ?.map((produto) => {
            const quantidadeSelecionada = selecionados
                .filter((p) => p.produto.id === produto.produto.id)
                .reduce((acc, p) => acc + (p.quantidadeSelecionada || 0), 0);

            const qtdBase = qtdPorProduto[produto.produto.id] || 0;

            return {
                ...produto,
                qtd_disponivel: qtdBase - quantidadeSelecionada,
            };
        })
        .filter((p) => {
            const nomeCorresponde = p.produto.nome
                .toLowerCase()
                .includes(filtro.toLowerCase());

            const jaSelecionado = selecionados.some(
                (s) => s.produto.id === p.produto.id
            );

            return nomeCorresponde && !jaSelecionado;
        });

    const selecionadosAtualizados = useMemo(() => {
        if (!Array.isArray(selecionados)) return [];

        return selecionados.map((item) => {
            const categoriaId = item.produto.categorias?.id;

            const quantidadeTotalSelecionadaCategoria = selecionados
                .filter((s) => s.produto.categorias?.id === categoriaId)
                .reduce((acc, s) => acc + (s.quantidadeSelecionada || 0), 0);

            const original = disponiveis.find((p) => p.produto.id === item.produto.id);

            const novaQtdDisponivel =
                (original?.quantidade_maxima ?? original?.qtd_disponivel ?? 0) - quantidadeTotalSelecionadaCategoria;

            return {
                ...item,
                qtd_disponivel: novaQtdDisponivel,
            };
        });
    }, [selecionados, disponiveis]);

    const moverParaSelecionados = (produto) => {
        const produtoJaSelecionado = selecionados.some(
            (s) => s.produto.id === produto.produto.id
        );

        if (produtoJaSelecionado) return;

        if (tipoRequisicao === 'extra') {
            setSelecionados((prev) => [
                ...prev,
                {
                    ...produto,
                    quantidadeSelecionada: 1,
                    variacaoSelecionada: '',
                    justificativaSelecionada: '',
                    marcarComoEntregue: false,
                    id_unico: crypto.randomUUID(),
                },
            ]);
        } else {
            const filhos = produto.produto.filhos || [];
            const variacaoUnica = filhos.length === 1 ? filhos[0].id : '';

            setSelecionados((prev) => [
                ...prev,
                {
                    ...produto,
                    quantidadeSelecionada: 1,
                    variacaoSelecionada: variacaoUnica,
                    id_unico: crypto.randomUUID(),
                },
            ]);
        }
    };

    const moverParaDisponiveis = (item) => {
        setSelecionados((prev) =>
            prev.filter((p) => p.id_unico !== item.id_unico)
        );
    };

    const onVariacaoChange = (item, novaVariacaoId) => {
        const jaExiste = selecionados.some(
            (p) =>
                p.produto.id === item.produto.id &&
                p.variacaoSelecionada === novaVariacaoId &&
                p.id_unico !== item.id_unico
        );

        if (jaExiste) return;

        setSelecionados((prev) =>
            prev.map((p) =>
                p.id_unico === item.id_unico
                    ? { ...p, variacaoSelecionada: novaVariacaoId }
                    : p
            )
        );
    };

    const onQuantidadeChange = (idUnico, novaQuantidade) => {
        setSelecionados((prev) =>
            prev.map((item) =>
                item.id_unico === idUnico
                    ? { ...item, quantidadeSelecionada: novaQuantidade }
                    : item
            )
        );
    };

    // Nova função para alterar justificativa individual
    const onJustificativaChange = (idUnico, justificativaId) => {
        setSelecionados((prev) =>
            prev.map((item) =>
                item.id_unico === idUnico
                    ? { ...item, justificativaSelecionada: justificativaId }
                    : item
            )
        );
    };

    // Nova função para alterar marcar como entregue individual
    const onMarcarComoEntregueChange = (idUnico, marcarEntregue) => {
        setSelecionados((prev) =>
            prev.map((item) =>
                item.id_unico === idUnico
                    ? { ...item, marcarComoEntregue: marcarEntregue }
                    : item
            )
        );
    };

    useEffect(() => {
        const saldoComPosicoes = produtos.length > 0 && produtos?.map((item, index) => ({
            ...item,
            posicaoOriginal: index,
        }));
        setDisponiveis(saldoComPosicoes);
    }, [produtos]);

    useEffect(() => {
        setProdutosSelecionados((prev) => {
            const igual = JSON.stringify(prev) === JSON.stringify(selecionadosAtualizados);
            return igual ? prev : selecionadosAtualizados;
        });
    }, [selecionadosAtualizados, setProdutosSelecionados]);

    useEffect(() => {
        if (selectedPessoa?.supervisor?.idSupervisor === dadosUsuario.pessoa.id_pessoa) {
            setAllowEntrega(true);
        }
    }, [selectedPessoa]);

    return (
        <>
            <Box
                sx={{
                    height: 450,
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    marginTop: 2,
                }}
            >
                <Box sx={{
                    flex: 1,
                    overflowY: 'auto',
                    pb: '120px'
                }}>
                    {isLoading ? (
                        <CircularProgress sx={{ margin: 'auto', width: '100%' }} />
                    ) : (
                        <Grid container spacing={2}>
                            {/* Produtos disponíveis */}
                            <Grid item xs={6}>
                                <Box
                                    sx={{
                                        border: '1px solid #ccc',
                                        borderRadius: 1,
                                        p: 2,
                                        minHeight: 400,
                                    }}
                                >
                                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                                        Produtos disponíveis
                                    </Typography>

                                    <TextField
                                        size="small"
                                        label="Buscar produto"
                                        variant="outlined"
                                        fullWidth
                                        value={filtro}
                                        onChange={(e) => setFiltro(e.target.value)}
                                        sx={{ mb: 2 }}
                                    />

                                    {disponiveisFiltrados?.length > 0 ? (
                                        disponiveisFiltrados.map((produto) =>
                                            renderCard(
                                                produto,
                                                moverParaSelecionados,
                                                false,
                                                onVariacaoChange,
                                                onQuantidadeChange,
                                                onJustificativaChange,
                                                onMarcarComoEntregueChange,
                                                selecionados,
                                                tipoRequisicao,
                                                justificativas
                                            )
                                        )
                                    ) : (
                                        <Typography>Nenhum produto encontrado.</Typography>
                                    )}
                                </Box>
                            </Grid>

                            {/* Produtos selecionados */}
                            <Grid item xs={6}>
                                <Box
                                    sx={{
                                        border: '1px solid #ccc',
                                        borderRadius: 1,
                                        p: 2,
                                        minHeight: 400,
                                    }}
                                >
                                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                                        Produtos selecionados
                                    </Typography>

                                    {selecionadosAtualizados?.length > 0 ? (
                                        selecionadosAtualizados.map((produto) =>
                                            renderCard(
                                                produto,
                                                moverParaDisponiveis,
                                                true,
                                                onVariacaoChange,
                                                onQuantidadeChange,
                                                onJustificativaChange,
                                                onMarcarComoEntregueChange,
                                                selecionados,
                                                tipoRequisicao,
                                                justificativas,
                                                allowEntrega
                                            )
                                        )
                                    ) : (
                                        <Typography>Nenhum produto selecionado.</Typography>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    )}
                </Box>

                {/* Rodapé fixo dentro do modal */}
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        p: 1.5,
                        backgroundColor: 'white',
                    }}
                >
                    {(
                        produtosSelecionados &&
                        produtosSelecionados.length > 0 &&
                        ((tipoRequisicao === 'extra' && !produtosSelecionados.some(p => !p.justificativaSelecionada)) ||
                        (tipoRequisicao !== 'extra' && !produtosSelecionados.some(p => !p.variacaoSelecionada)))
                    ) ? (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                backgroundColor: '#E8F5E9',
                                border: '1px solid #4CAF50',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1.5,
                                transition: 'all 0.3s ease',
                            }}
                        >
                            <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 22 }} />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#1B5E20',
                                    fontWeight: 500,
                                    textAlign: 'center',
                                }}
                            >
                                EPI's selecionados. Clique em <strong>"Confirmar"</strong> para confirmar a operação.
                            </Typography>
                        </Paper>
                    ) : (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                backgroundColor: '#FFF8E1',
                                border: '1px solid #FFB300',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1.5,
                                transition: 'all 0.3s ease',
                            }}
                        >
                            <HourglassBottomIcon sx={{ color: '#FB8C00', fontSize: 22 }} />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#E65100',
                                    fontWeight: 500,
                                    textAlign: 'center',
                                }}
                            >
                                Aguardando seleção de EPI's...
                            </Typography>
                        </Paper>
                    )}
                </Box>
            </Box>
        </>
    );
}